// ProjectProgress.jsx - Project progress tracking (Admin only)
import React from 'react';
import { Card, Progress, Row, Col, Typography, Space, Tag } from 'antd';
import { 
  ProjectOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const ProjectProgress = () => {
  // Mock project data - replace with real data
  const projectStats = {
    total: 24,
    completed: 18,
    inProgress: 4,
    pending: 2,
    overdue: 1
  };

  const recentProjects = [
    {
      name: 'E-commerce Platform',
      progress: 85,
      status: 'in-progress',
      deadline: '2024-02-15',
      team: 'Development'
    },
    {
      name: 'Mobile App Redesign',
      progress: 60,
      status: 'in-progress',
      deadline: '2024-02-20',
      team: 'Design'
    },
    {
      name: 'API Documentation',
      progress: 100,
      status: 'completed',
      deadline: '2024-01-30',
      team: 'Development'
    },
    {
      name: 'Brand Identity',
      progress: 30,
      status: 'pending',
      deadline: '2024-03-01',
      team: 'Design'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'in-progress': return 'blue';
      case 'pending': return 'orange';
      case 'overdue': return 'red';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleOutlined />;
      case 'in-progress': return <ClockCircleOutlined />;
      case 'pending': return <ExclamationCircleOutlined />;
      default: return <ProjectOutlined />;
    }
  };

  return (
    <Card 
      title={
        <div className="flex items-center space-x-2">
          <ProjectOutlined className="text-accent-blue" />
          <span>Project Progress Overview</span>
        </div>
      }
      className="shadow-medium border border-border-light rounded-xl"
    >
      {/* Quick Stats */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col span={6}>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <Title level={3} className="m-0 text-accent-blue">
              {projectStats.total}
            </Title>
            <Text className="text-text-secondary">Total Projects</Text>
          </div>
        </Col>
        <Col span={6}>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <Title level={3} className="m-0 text-accent-green">
              {projectStats.completed}
            </Title>
            <Text className="text-text-secondary">Completed</Text>
          </div>
        </Col>
        <Col span={6}>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <Title level={3} className="m-0 text-accent-orange">
              {projectStats.inProgress}
            </Title>
            <Text className="text-text-secondary">In Progress</Text>
          </div>
        </Col>
        <Col span={6}>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <Title level={3} className="m-0 text-accent-red">
              {projectStats.overdue}
            </Title>
            <Text className="text-text-secondary">Overdue</Text>
          </div>
        </Col>
      </Row>

      {/* Recent Projects */}
      <div>
        <Title level={4} className="text-text-primary mb-4">
          Recent Projects
        </Title>
        <Space direction="vertical" size="middle" className="w-full">
          {recentProjects.map((project, index) => (
            <div 
              key={index}
              className="p-4 border border-border-light rounded-lg hover:shadow-soft transition-all"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span className="text-text-primary font-semibold">
                    {project.name}
                  </span>
                  <Tag 
                    color={getStatusColor(project.status)}
                    icon={getStatusIcon(project.status)}
                  >
                    {project.status.replace('-', ' ').toUpperCase()}
                  </Tag>
                  <Tag color="default">{project.team}</Tag>
                </div>
                <Text className="text-text-secondary text-sm">
                  Due: {new Date(project.deadline).toLocaleDateString()}
                </Text>
              </div>
              <div className="flex items-center space-x-3">
                <Progress 
                  percent={project.progress} 
                  strokeColor={
                    project.progress === 100 ? '#10b981' : 
                    project.progress >= 70 ? '#19868d' : 
                    project.progress >= 40 ? '#f97316' : '#ef4444'
                  }
                  trailColor="#f1f5f9"
                  className="flex-1"
                />
                <Text className="text-text-secondary font-medium min-w-12">
                  {project.progress}%
                </Text>
              </div>
            </div>
          ))}
        </Space>
      </div>
    </Card>
  );
};

export default ProjectProgress;